import { QuestionState, SenderStatus } from '../../constans/question-enums';
import * as mobx from 'mobx';
import { removeExtraSpaces } from '../../utils/helpers/data-processing-helpers';
import { getGMTDate } from '../../utils/helpers/date-helper';
import { ManageQuestionsNewLabel } from './manage-questions-new-label/manage-questions-new-label';
import { getManageQuestionsNewLabel } from './manage-questions-new-label/manage-questions-new-label-instance';

export interface IQuestion {
    messageId: string;
    message: string;
    presentationId: string;
    prioritizeIndex: number;
    senderEmail: string;
    senderName: string;
    sentDate: string;
    state: QuestionState | string;
    lastChanges: string;
    presentationTitle? : string;
    senderStatus?: SenderStatus | null;
    aiResponse?: string | null;
    topic?: string;
}

export interface IQueryBody {
  lastChanges: string;
  prioritizeIndex: number;
  messageId: string | null;
  state: QuestionState | string;
}

export type TQuestionEntity = Omit<QuestionEntity, 'editableState' | 'questionCopy' | 'isNew' | 'timeOut' | 'isHighlighted'>

export enum EditableState {
    CLOSED = 'CLOSED',
    OPENED = 'OPENED',
}

class QuestionEntity implements IQuestion {
  messageId: string;
  message: string;
  presentationId: string;
  prioritizeIndex: number;
  senderEmail: string;
  senderName: string;
  sentDate: string;
  state: QuestionState | string;
  lastChanges: string;
  presentationTitle? : string;
  senderStatus?: SenderStatus | null;
  editableState: EditableState;
  questionCopy: IQuestion | null;
  isNew: boolean;
  isHighlighted: boolean;
  topic: string;
  aiResponse?: string | null;
  private timeOut: ReturnType<typeof setTimeout> | null;

  constructor (
    fromDTO: IQuestion,
      private readonly manageQuestionsNewLabel: ManageQuestionsNewLabel = getManageQuestionsNewLabel(),
  ) {
    mobx.makeAutoObservable(this);
    this.deserialize(fromDTO);
    this.questionCopy = null;
    this.editableState = EditableState.CLOSED;
    this.isNew = false;
    this.timeOut = null;
    this.isHighlighted = false;
  }

  public setQuestionProperty (prioritizeIndex: number, state: QuestionState | string, lastChanges: string): void {
    this.clearPrevTimeOut(state);
    this.prioritizeIndex = prioritizeIndex;
    this.state = state;
    this.lastChanges = lastChanges;
  }

  public applyChangedFields ({ senderName, senderEmail, lastChanges, message, sentDate, senderStatus }: IQuestion): void {
    this.message = message;
    this.senderEmail = senderEmail;
    this.senderName = senderName;
    this.sentDate = sentDate;
    this.lastChanges = lastChanges;
    this.senderStatus = senderStatus;
    this.editableState = EditableState.CLOSED;
    this.questionCopy = null;
  }

  public updateOnSubscription (fromDTO: IQuestion): void {
    this.clearPrevTimeOut(fromDTO.state);
    this.deserialize(fromDTO);
  }

  public updateOnResponse (fromDTO: IQuestion): void {
    this.clearPrevTimeOut(fromDTO.state);
    this.deserialize(fromDTO);
  }

  public updateEntityOnCopy (): void {
    if (!this.questionCopy) return;
    this.updateOnSubscription(this.questionCopy);
  }

  public setIsEditable (state:EditableState):void {
    this.editableState = state;
  }

  public getEditableState ():EditableState {
    return this.editableState;
  }

  public setQuestionCopy (copy:IQuestion): void {
    this.questionCopy = copy;
  }

  public applyNewLabel (): void {
    this.setStatusNew();
    this.removeStatusNew();
  }

  public applyHighlight (): void {
    this.setHighlight();
    this.removeHighlight();
  }

  private setStatusNew (): void {
    if(!this.state) return;
    this.manageQuestionsNewLabel.increment(this.state);
    this.isNew = true;
  }

  private clearPrevTimeOut (state: QuestionState | string): void {
    const isStateDifferent = this.state !== state;
    if(this.timeOut && isStateDifferent) {
      clearTimeout(this.timeOut);
      this.manageQuestionsNewLabel.decrement(this.state);
      this.isNew = false;
      this.isHighlighted = false;
    }
  }

  private removeStatusNew (): void {
    if(!this.state) return;
    const delay = 3000;
    this.timeOut = setTimeout(() => {
      this.manageQuestionsNewLabel.decrement(this.state);
      this.isNew = false;

      if(this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }, delay);
  }

  private setHighlight (): void {
    this.isHighlighted = true;
  }

  private removeHighlight (): void {
    const delay = 3000;

    this.timeOut = setTimeout(() => {
      this.isHighlighted = false;
      if(this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }, delay);

  }

  private deserialize (fromDTO: IQuestion):void {
    this.message = fromDTO.message;
    this.messageId = fromDTO.messageId;
    this.state = fromDTO.state;
    this.presentationId = fromDTO.presentationId;
    this.prioritizeIndex = fromDTO.prioritizeIndex;
    this.senderName = fromDTO.senderName;
    this.sentDate = fromDTO.sentDate;
    this.lastChanges = fromDTO.lastChanges;
    this.senderEmail = fromDTO.senderEmail;
    this.senderStatus = fromDTO?.senderStatus;
    this.topic = fromDTO?.topic || '';
    this.aiResponse = fromDTO?.aiResponse || null;
  }

  public toDTO (): IQuestion {
    return {
      message: removeExtraSpaces(this.message),
      messageId: this.messageId,
      state: this.state,
      presentationId :this.presentationId,
      prioritizeIndex:this.prioritizeIndex,
      senderName : removeExtraSpaces(this.senderName),
      sentDate : this.sentDate,
      lastChanges : getGMTDate(),
      senderEmail : this.senderEmail,
      senderStatus : this.senderStatus,
      topic: this.topic,
    };
  }

}

export default QuestionEntity;
